import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Clock,
  Eye,
  History,
  MoreHorizontal,
  Receipt,
  User,
  XCircle,
} from "lucide-react";
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui";
import {
  EmptyState,
  PageError,
  PageLoader,
  DataTable,
  type Column,
} from "@/components/common";
import { cn, formatCurrency, formatDateTime } from "@/lib/utils";
import { useDebounce } from "@/hooks";
import { useOrderHistory } from "../api";
import { OrderHistoryFilters } from "./OrderHistoryFilters";
import { RefundBadge } from "./RefundBadge";
import { statusConfig } from "../lib/status-config";
import type { Order } from "@/types";

interface OrderHistoryViewProps {
  shiftId?: string;
  onClearShiftFilter: () => void;
  setSelectedOrder: (order: Order | null) => void;
}

const PAGE_SIZE = 10;

export function OrderHistoryView({
  shiftId,
  onClearShiftFilter,
  setSelectedOrder,
}: OrderHistoryViewProps) {
  const navigate = useNavigate();
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  const debouncedSearch = useDebounce(search, 300);

  const {
    data: ordersData,
    isLoading,
    isFetching,
    error,
    refetch,
  } = useOrderHistory({
    ...(startDate && { startDate }),
    ...(endDate && { endDate }),
    ...(shiftId && { shiftId }),
    ...(debouncedSearch && { search: debouncedSearch }),
    page,
    limit: PAGE_SIZE,
  });

  const orders = ordersData?.data ?? [];
  const pagination = ordersData?.pagination;

  const handleSearchChange = (value: string) => {
    setSearch(value);
    setPage(1);
  };

  const handleClearAll = () => {
    setStartDate("");
    setEndDate("");
    setSearch("");
    setPage(1);
  };

  const handleStartDateChange = (value: string) => {
    setStartDate(value);
    setPage(1);
  };

  const handleEndDateChange = (value: string) => {
    setEndDate(value);
    setPage(1);
  };

  const getOrderContentsSummary = (order: Order): string => {
    if (order.items.length > 0) {
      return order.items
        .map((item) => `${item.quantity}x ${item.productName}`)
        .join(", ");
    }

    const count = order.itemCount ?? 0;
    
    return `${count} ${count === 1 ? "item" : "items"}`;
  };

  const columns: Column<Order>[] = [
    {
      key: "orderNumber",
      header: "Order",
      cell: (order) => {
        const summary = getOrderContentsSummary(order);

        return (
          <span className="block max-w-[320px] truncate font-medium" title={summary}>
            {summary}
          </span>
        );
      },
    },
    {
      key: "status",
      header: "Status",
      cell: (order) => {
        const config = statusConfig[order.status];

        return (
          <div className="flex items-center gap-2">
            <Badge variant="outline" className={cn("text-xs", config.color)}>
              {config.label}
            </Badge>
            <RefundBadge order={order} className="text-xs" />
          </div>
        );
      },
    },
    {
      key: "type",
      header: "Type",
      cell: (order) => (
        <Badge
          variant={order.orderType === "takeaway" ? "default" : "secondary"}
          className="text-xs"
        >
          {order.orderType === "takeaway" ? "Takeaway" : "Dine In"}
        </Badge>
      ),
    },
    {
      key: "customer",
      header: "Customer",
      cell: (order) =>
        order.customer ? (
          <span className="flex items-center gap-1">
            <User className="h-3 w-3 text-muted-foreground" />
            {order.customer.name}
          </span>
        ) : (
          <span className="text-muted-foreground">-</span>
        ),
    },
    {
      key: "items",
      header: "Items",
      cell: (order) => order.itemCount ?? order.items.length,
    },
    {
      key: "total",
      header: "Total",
      cell: (order) => (
        <span className="font-medium">{formatCurrency(order.total)}</span>
      ),
    },
    {
      key: "date",
      header: "Date",
      cell: (order) => (
        <span className="text-muted-foreground">
          {formatDateTime(order.createdAt)}
        </span>
      ),
    },
    {
      key: "actions",
      header: "Actions",
      className: "w-[100px]",
      cell: (order) => (
        <div onClick={(e) => e.stopPropagation()}>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon">
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => setSelectedOrder(order)}>
                <Eye className="mr-2 h-4 w-4" />
                View Details
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => navigate(`/orders/${order.id}/invoice`)}
              >
                <Receipt className="mr-2 h-4 w-4" />
                View Invoice
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      ),
    },
  ];

  const renderMobileCard = (order: Order) => {
    const config = statusConfig[order.status];
    const summary = getOrderContentsSummary(order);

    return (
      <div
        className="cursor-pointer rounded-lg border p-4 transition-colors hover:bg-muted/50"
        onClick={() => setSelectedOrder(order)}
      >
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <span className="max-w-[220px] truncate font-bold" title={summary}>
                {summary}
              </span>
              <Badge variant="outline" className={cn("text-xs", config.color)}>
                {config.label}
              </Badge>
              <Badge
                variant={order.orderType === "takeaway" ? "default" : "secondary"}
                className="text-xs"
              >
                {order.orderType === "takeaway" ? "Takeaway" : "Dine In"}
              </Badge>
              <RefundBadge order={order} className="text-xs" />
            </div>
            <div className="mt-1 text-sm text-muted-foreground">
              {formatDateTime(order.createdAt)}
            </div>
            {order.customer && (
              <div className="mt-0.5 flex items-center gap-1 text-sm text-muted-foreground">
                <User className="h-3 w-3" />
                {order.customer.name}
              </div>
            )}
          </div>
          <div
            className="flex items-start gap-2"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="text-right">
              <p className="font-bold">{formatCurrency(order.total)}</p>
              <p className="text-xs text-muted-foreground">
                {order.itemCount ?? order.items.length} items
              </p>
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="shrink-0">
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => setSelectedOrder(order)}>
                  <Eye className="mr-2 h-4 w-4" />
                  View Details
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => navigate(`/orders/${order.id}/invoice`)}
                >
                  <Receipt className="mr-2 h-4 w-4" />
                  View Invoice
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>
    );
  };

  if (isLoading && !ordersData) {
    return <PageLoader />;
  }

  if (error) {
    return (
      <PageError message="Failed to load order history" onRetry={refetch} />
    );
  }

  return (
    <div className="space-y-4">
      {shiftId && (
        <div className="flex items-center justify-between rounded-lg border border-blue-200 bg-blue-50 px-4 py-3">
          <div className="flex items-center gap-2 text-sm text-blue-700">
            <Clock className="h-4 w-4" />
            <span>Showing orders for this shift only</span>
          </div>
          <Button variant="ghost" size="sm" onClick={onClearShiftFilter}>
            <XCircle className="mr-1.5 h-4 w-4" />
            Clear filter
          </Button>
        </div>
      )}

      <Card>
        <CardHeader>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <CardTitle className="flex items-center gap-2">
              <History className="h-5 w-5" />
              Orders ({pagination?.total || 0})
            </CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          <OrderHistoryFilters
            search={search}
            onSearchChange={handleSearchChange}
            startDate={startDate}
            onStartDateChange={handleStartDateChange}
            endDate={endDate}
            onEndDateChange={handleEndDateChange}
            onClear={handleClearAll}
          />
          <div className="mt-4">
            {orders.length === 0 ? (
              <EmptyState
                icon={History}
                title="No orders found"
                description={
                  search || startDate || endDate
                    ? "Try adjusting your filters"
                    : "Completed orders will appear here"
                }
              />
            ) : (
              <DataTable
                data={orders}
                columns={columns}
                rowKey={(order) => order.id}
                renderMobileCard={renderMobileCard}
                onRowClick={setSelectedOrder}
                isFetching={isFetching}
                pagination={{
                  page,
                  pageSize: PAGE_SIZE,
                  total: pagination?.total ?? 0,
                  onPageChange: setPage,
                }}
              />
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
